import { useEffect, useMemo } from "react";

import { type QueryResult, useQuery } from "@apollo/client";
import { useSetAtom } from "jotai";
// Recoil is deprecated. Repo is archived as of Jan 1 2025, so from now on we are using jotai
import { useSetRecoilState } from "recoil";

import { LensCreatorPayoutEligibility } from "../../gql-generated/types";
import { isVideoSrcExtension } from "../../lens-preview/validation";
import { type ApolloResult } from "../../shared/models";
import {
    DEFAULT_PREVIEW_NAME,
    lensCtaAtomById,
    primaryCategoryIdAtomById,
    secondaryCategoryIdAtomById,
} from "../../shared/state";
import { lensPreviewMediaItemsAtomById } from "../../shared/state/LensPreviewMediaState";
import { type GetLensInfoQuery, type GetLensInfoQueryVariables, getLensInfo } from "../api/queries";
import { type LensEditableState, lensEditedStateAtom, lensStateAtom } from "../state/lensImmerState";

export const useLens = (
    lensId: string,
    skip = false,
    withOrganizationId = false,
): ApolloResult<{
    data: GetLensInfoQuery | undefined;
    refetch?: QueryResult<GetLensInfoQuery, GetLensInfoQueryVariables>["refetch"];
}> => {
    const { loading, data, error, refetch } = useQuery<GetLensInfoQuery, GetLensInfoQueryVariables>(getLensInfo, {
        variables: {
            lensId,
            withOrganizationId,
        },
        skip,
    });

    // Recoil state setters (submission flow)
    const setLensPreviewMediaItems = useSetRecoilState(lensPreviewMediaItemsAtomById(lensId));
    const setPrimaryCategory = useSetRecoilState(primaryCategoryIdAtomById(lensId));
    const setSecondaryCategory = useSetRecoilState(secondaryCategoryIdAtomById(lensId));
    const setLensCta = useSetRecoilState(lensCtaAtomById(lensId));

    // Jotai state setters (lens details)
    const setLensState = useSetAtom(lensStateAtom(lensId));
    const setEditedState = useSetAtom(lensEditedStateAtom(lensId));

    const stableLensState = useMemo(() => {
        if (!data?.getLens.lens) return null;

        const {
            lensCreatorPayoutEligibility,
            description,
            isGameUserProvided,
            lensCta,
            previewMediaList,
            spectaclesTags,
            tagsList,
            visibility,
        } = data.getLens.lens;

        return {
            creatorRewardProgramEnrolled:
                lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible,
            description: description || "",
            isGameUserProvided,
            lensCta: lensCta ?? null,
            previewMediaItems:
                previewMediaList?.map((media) => ({
                    file: null,
                    url: media.standardMediaUrl,
                    isVideo: media.standardMediaUrl.includes(".mp4") || media.standardMediaUrl.includes(".mov"),
                    name: `Preview ${media.mediaId}`,
                    type: "media" as const,
                    mediaId: media.mediaId,
                    isLensStudioPreview: false,
                })) ?? [],
            spectaclesTags: [...(spectaclesTags ?? [])],
            tags: [...tagsList],
            profileVisibility: visibility,
        } satisfies Partial<LensEditableState>;
    }, [data?.getLens.lens]);

    useEffect(() => {
        if (data?.getLens.lens) {
            const { previewMediaList, primaryCategoryId, secondaryCategoryId, lensCta } = data.getLens.lens;

            // Update Recoil state (submission flow)
            if (previewMediaList) {
                setLensPreviewMediaItems(
                    previewMediaList.map((media) => ({
                        file: null,
                        url: media.standardMediaUrl,
                        isVideo: isVideoSrcExtension(media.standardMediaUrl),
                        name: DEFAULT_PREVIEW_NAME,
                        type: "media",
                        mediaId: media.mediaId,
                    })),
                );
            }

            if (primaryCategoryId) {
                setPrimaryCategory(primaryCategoryId);
            }

            if (secondaryCategoryId) {
                setSecondaryCategory(secondaryCategoryId);
            }

            if (lensCta) {
                setLensCta(lensCta);
            }
        }
    }, [data, setLensPreviewMediaItems, setPrimaryCategory, setSecondaryCategory, setLensCta]);

    // Update Jotai state when stable state changes
    useEffect(() => {
        if (stableLensState) {
            setLensState(stableLensState);
            setEditedState(stableLensState);
        }
    }, [stableLensState, setLensState, setEditedState]);

    return { loading, data, error, refetch };
};
